<script setup>
// 골드나라 웹 공통 상단 — 모바일 (시세바 40 + 헤더 56 = 96px)
//
// Figma: xsS20cl9Iwm2XO7WPQkg54 / Mobile 페이지
//   상단 2004:3026 (시세바 2004:3028 + 헤더 2004:3044)
//   메뉴 2004:3454 (햄버거를 누르면 덮는 전체화면)
//
// 데스크톱(SiteHeader.vue)과 구조가 달라 파일을 나눴다.
//   시세바  4종 → GOLD 1종
//   메뉴    가로 6개 → 햄버거 + 전체화면 오버레이
//
// ★ 이 파일은 세 저장소에 "복사"해서 쓴다.
//     homepage / goldnaramall-client / mediahub-client
//   고칠 때는 세 곳을 모두 같이 고칠 것.
//
// 메뉴 항목과 링크는 SiteHeader.vue 와 같은 표를 쓴다. 한쪽만 고치면 화면마다 달라진다.
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { SITE_MENU } from '@/data/siteMenu.js'
import RatesTicker from '@/components/common/RatesTicker.vue'
import imgTickerGlow from '@/assets/figma/d13030e647b9e060f2375cad0d1c77f92d50085f.png'
import imgArrowDown from '@/assets/figma/b3890e2308dd6bf38f97236a9549a4b80c6c9e5b.svg'
import imgArrowUp from '@/assets/figma/5e22dcd217a5a0d68a17d4fabf05228f570365fd.svg'
import imgGlobe from '@/assets/figma/c1e014a3b0ac8880c90a4491cfa8f992b1925f96.svg'
import imgLogo from '@/assets/logo-goldnara.svg'

const props = defineProps({
  // 현재 화면에 해당하는 메뉴 key. 메뉴를 열었을 때 해당 항목만 강조된다.
  active: { type: String, default: '' },
  // 로그인·로그아웃 버튼 노출. 회원 기능이 있는 자사몰에서만 켠다.
  showLogin: { type: Boolean, default: false },
  loggedIn: { type: Boolean, default: false },
  // 시세. 모바일은 맨 앞 1종만 보여 준다.
  rates: { type: Array, default: null },
})

const emit = defineEmits(['login', 'logout', 'rates-toggle', 'language-toggle'])

// 시세 기본값. rates 를 넘기지 않은 화면은 이 값이 보인다(데스크톱 SiteHeader 와 같다).
// 퍼블리싱은 자리가 좁아 맨 앞 한 종만 보여 줬지만, 흐르게 하면 네 종을 다 볼 수 있다.
const DEFAULT_RATES = [
  { label: 'GOLD', price: '951,000원', change: '14,000', direction: 'down' },
  { label: 'Silver', price: '14,490원', change: '690', direction: 'up' },
  { label: '중매거래', price: '951,000원', change: '14,000', direction: 'down' },
  { label: '즉시거래', price: '951,000원', change: '14,000', direction: 'down' },
]
const tickerItems = computed(() => (props.rates?.length ? props.rates : DEFAULT_RATES))

// 칸 사이 간격. 데스크톱(60)보다 좁게 둔다 — 글자도 작고 화면도 좁다.
const TICKER_GAP = 24

// 메뉴 표는 푸터와 같이 쓴다 — data/siteMenu.js
const menu = SITE_MENU

// 지구본(언어 선택)을 가린다. 다국어 판이 아직 없다(2026-11-04 지시). 데스크톱 SiteHeader 의
// SHOW_LANGUAGE 와 같은 뜻이다 — 다국어 판이 나오면 true 로 되돌리면 된다.
const SHOW_LANGUAGE = false

// 하위 메뉴(REAL GOLD TRADE 의 소개 · 오늘의 시세) 중 지금 화면. 주소의 경로로 가른다 —
// 메뉴 주소는 절대 주소라 도메인까지 맞추면 로컬에서 안 맞는다. 바깥 칸(active)이 같을 때만 본다.
const currentPath = typeof window === 'undefined' ? '' : window.location.pathname.replace(/\/+$/, '')
const isCurrentSub = (item, sub) => {
  if (props.active !== item.key) return false
  try {
    return new URL(sub.href).pathname.replace(/\/+$/, '') === currentPath
  } catch {
    return false
  }
}

const open = ref(false)

// 메뉴가 떠 있는 동안 뒤 화면이 따라 스크롤되지 않게 한다.
watch(open, (on) => {
  document.body.style.overflow = on ? 'hidden' : ''
})
onBeforeUnmount(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <header class="relative w-full">
    <!-- 시세바 2004:3028 -->
    <div class="relative h-[40px] w-full overflow-hidden bg-black">
      <div
        aria-hidden
        class="pointer-events-none absolute left-[28px] top-[-48px] flex h-[126px] w-[183px] items-center justify-center"
      >
        <div class="flex-none rotate-[-11.89deg]">
          <div class="relative h-[94px] w-[167px]">
            <img alt="" class="absolute inset-0 size-full max-w-none object-cover" :src="imgTickerGlow" />
          </div>
        </div>
      </div>

      <!-- 퍼블리싱은 라벨과 시세를 좌우 끝에 붙여 놓았지만, 시세가 흐르려면 남는 자리를
           통째로 써야 한다. 라벨은 왼쪽에 고정하고 그 오른쪽을 띠가 가져간다. -->
      <div class="relative flex h-full items-center gap-[12px] px-[15px]">
        <span class="shrink-0 whitespace-nowrap text-[14px] font-semibold leading-none text-white">오늘의 시세</span>

        <RatesTicker class="h-full flex-1" :items="tickerItems" :gap="TICKER_GAP">
          <template #default="{ item }">
            <p class="whitespace-nowrap text-[13px] font-medium leading-[18px] text-white">{{ item.label }}</p>
            <p class="ml-[5px] whitespace-nowrap text-[13px] font-semibold leading-[18px] text-white">
              {{ item.price }}
            </p>
            <div class="ml-[5px] flex shrink-0 items-center gap-[2px]">
              <img alt="" class="size-[12px] shrink-0" :src="item.direction === 'up' ? imgArrowUp : imgArrowDown" />
              <p
                class="whitespace-nowrap text-[12px] font-semibold leading-[18px]"
                :class="item.direction === 'up' ? 'text-[#8cbaff]' : 'text-[#ff4372]'"
              >
                {{ item.change }}
              </p>
            </div>
          </template>
        </RatesTicker>
      </div>
    </div>

    <!-- 헤더 2004:3044. 로고는 2026-11-06 에 2/3 로 줄였다(130x28 -> 86.67x18.67, 펼친 메뉴의 로고도 같이) —
         "모든 페이지 헤더의 골드나라 로고의 크기를 2/3으로". 데스크톱 SiteHeader 도 184x40 -> 122.67x26.67. -->
    <div class="relative h-[56px] w-full border-b border-solid border-[#e2e2e2] bg-white">
      <a href="/" class="absolute left-[15px] top-1/2 block h-[18.67px] w-[86.67px] -translate-y-1/2" aria-label="골드나라">
        <img alt="골드나라" class="h-full w-full object-contain object-left" :src="imgLogo" />
      </a>
      <!-- actions 슬롯: 저장소마다 다른 아이콘(자사몰의 검색·장바구니)이 햄버거 왼쪽에 붙는다.
           슬롯이 비면 아무것도 그리지 않으므로 홈페이지·미디어 허브는 그대로다. -->
      <div class="absolute right-[15px] top-1/2 flex -translate-y-1/2 items-center gap-[14px]">
        <slot name="actions" />
        <button
          type="button"
          class="flex size-[30px] items-center justify-center"
          aria-label="메뉴 열기"
          :aria-expanded="open"
          @click="open = true"
        >
          <svg class="size-[22px]" viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
            <path d="M3 6h16M3 11h16M3 16h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 메뉴 2004:3454 — 햄버거를 누르면 화면을 덮는다.
         body 로 옮겨 붙인다. 모바일 화면은 본문 캔버스에 transform: scale() 이 걸려 있고,
         그 안에서는 position: fixed 가 뷰포트가 아니라 캔버스를 기준으로 잡힌다 —
         스크롤을 내린 뒤 메뉴를 열면 화면 밖(캔버스 맨 위)에 그려져 아무것도 안 보인다. -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-50 flex flex-col bg-white">
        <!-- 시세바 2004:3523 -->
        <div class="relative h-[40px] w-full shrink-0 overflow-hidden bg-black">
          <!-- 데스크톱과 같은 장식이다. 시세가 이 위를 흘러가므로 밝기를 낮춘다.
               (SiteHeader.vue 의 같은 자리 주석 참고) -->
          <div
            aria-hidden
            class="pointer-events-none absolute left-[28px] top-[-48px] flex h-[126px] w-[183px] items-center justify-center opacity-40"
          >
            <div class="flex-none rotate-[-11.89deg]">
              <div class="relative h-[94px] w-[167px]">
                <img alt="" class="absolute inset-0 size-full max-w-none object-cover" :src="imgTickerGlow" />
              </div>
            </div>
          </div>
          <div class="relative flex h-full items-center gap-[12px] px-[15px]">
            <span class="shrink-0 whitespace-nowrap text-[14px] font-semibold leading-none text-white">오늘의 시세</span>
            <RatesTicker class="h-full flex-1" :items="tickerItems" :gap="TICKER_GAP">
              <template #default="{ item }">
                <p class="whitespace-nowrap text-[13px] font-medium leading-[18px] text-white">{{ item.label }}</p>
                <p class="ml-[5px] whitespace-nowrap text-[13px] font-semibold leading-[18px] text-white">
                  {{ item.price }}
                </p>
                <div class="ml-[5px] flex shrink-0 items-center gap-[2px]">
                  <img alt="" class="size-[12px] shrink-0" :src="item.direction === 'up' ? imgArrowUp : imgArrowDown" />
                  <p
                    class="whitespace-nowrap text-[12px] font-semibold leading-[18px]"
                    :class="item.direction === 'up' ? 'text-[#8cbaff]' : 'text-[#ff4372]'"
                  >
                    {{ item.change }}
                  </p>
                </div>
              </template>
            </RatesTicker>
          </div>
        </div>

        <!-- 메뉴 헤더 2004:3482 — 로고 · 언어 · 닫기. 언어(지구본)는 지금 가려 둔다(SHOW_LANGUAGE). -->
        <div class="relative h-[60px] w-full shrink-0">
          <a href="/" class="absolute left-[15px] top-1/2 block h-[18.67px] w-[86.67px] -translate-y-1/2" aria-label="골드나라">
            <img alt="골드나라" class="h-full w-full object-contain object-left" :src="imgLogo" />
          </a>
          <div class="absolute right-[15px] top-1/2 flex -translate-y-1/2 items-center gap-[16px]">
            <button
              v-if="SHOW_LANGUAGE"
              type="button"
              class="flex shrink-0 items-center"
              aria-label="언어 선택"
              @click="emit('language-toggle')"
            >
              <img alt="" class="size-[24px] shrink-0" :src="imgGlobe" />
            </button>
            <button type="button" class="flex size-[30px] items-center justify-center" aria-label="메뉴 닫기" @click="open = false">
              <svg class="size-[22px]" viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                <path d="M5 5l12 12M17 5L5 17" />
              </svg>
            </button>
          </div>
        </div>

        <!-- 메뉴 목록 2004:3455 — 항목 하나가 60px.
             하위 메뉴가 있는 항목(REAL GOLD TRADE)은 제목으로 두고 그 아래에 하위 항목을 늘 펼쳐 둔다
             (2026-11-04 지시: '늘 펼쳐 두기'). 그전에는 한 칸짜리 링크라 소개로만 가고 '오늘의 시세' 로 갈
             길이 없었다. 시안(옛 2004:3454 · 새 v1.1)에는 하위 메뉴 모양이 없다 — 들여쓰기 16, 줄 44,
             글자 15 / 500 / 회색은 여기서 정한 값이다. 지금 화면인 하위 항목만 금색이다. -->
        <nav class="mt-[11px] flex flex-col px-[15px]">
          <template v-for="item in menu" :key="item.key">
            <template v-if="item.children?.length">
              <p
                class="flex h-[60px] shrink-0 items-center text-[16px] leading-[24px]"
                :class="active === item.key ? 'font-semibold text-[#c4890b]' : 'font-medium text-black'"
              >
                {{ item.label }}
              </p>
              <div class="-mt-[8px] flex shrink-0 flex-col pb-[8px]">
                <a
                  v-for="sub in item.children"
                  :key="sub.key"
                  :href="sub.href"
                  class="flex h-[44px] shrink-0 items-center pl-[16px] text-[15px] leading-[22px]"
                  :class="isCurrentSub(item, sub) ? 'font-semibold text-[#c4890b]' : 'font-medium text-[#626161]'"
                  :aria-current="isCurrentSub(item, sub) ? 'page' : null"
                  @click="open = false"
                >
                  {{ sub.label }}
                </a>
              </div>
            </template>
            <a
              v-else
              :href="item.href"
              class="flex h-[60px] shrink-0 items-center text-[16px] leading-[24px]"
              :class="active === item.key ? 'font-semibold text-[#c4890b]' : 'font-medium text-black'"
              @click="open = false"
            >
              <span class="relative">
                {{ item.label }}
                <!-- 활성 표시 막대는 없앴다(2026-09-28 지시). 금색 글자만으로 표시한다. -->
              </span>
            </a>
          </template>
        </nav>

        <!-- 로그인 2004:3540 -->
        <!-- 퍼블리싱에는 버튼 위에 혜택 안내 말풍선(2004:3544)이 있지만 모바일에서는 두지 않는다.
             데스크톱 SiteHeader 의 말풍선은 그대로 쓴다. -->
        <div v-if="showLogin" class="relative mt-[24px] shrink-0 px-[15px]">
          <div class="flex items-center gap-[12px]">
            <button
              type="button"
              class="flex h-[46px] shrink-0 items-center justify-center rounded-[50px] bg-[#00264d] px-[26px]"
              @click="emit('login'); open = false"
            >
              <span class="whitespace-nowrap text-[16px] font-semibold leading-[24px] text-white">
                {{ loggedIn ? '마이페이지' : '로그인' }}
              </span>
            </button>
            <button
              v-if="loggedIn"
              type="button"
              class="shrink-0 whitespace-nowrap text-[15px] font-medium leading-[22px] text-[#626161]"
              @click="emit('logout'); open = false"
            >
              로그아웃
            </button>
          </div>
        </div>

        <p class="mt-auto px-[15px] pb-[24px] text-[13px] leading-[16px] text-[#969696]">
          Copyright © AURA. All rights reserved.
        </p>
      </div>
    </Teleport>
  </header>
</template>
